import {
  ARTBOARD_PAYLOAD_STORAGE_KEY,
  applyBrandingSnapshot,
  type ArtboardInbound,
  type ArtboardOutbound,
  type ArtboardPayload,
} from '@repo/api-client';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PreviewCanvasFrame } from './PreviewCanvasFrame';
import {
  A4_HEIGHT_PX,
  A4_WIDTH_PX,
  previewCanvasScale,
  scaledPreviewDimensions,
} from './previewCanvasConstants';

/**
 * Live, in-browser invoice render. Mirrors mobile's `TemplateCanvas`
 * but uses a same-origin `<iframe>` instead of `react-native-webview`.
 */
export interface TemplateCanvasProps {
  payload: ArtboardPayload;
  templateId?: string;
  width: number;
  className?: string;
  onReady?: (pageCount: number) => void;
}

export function TemplateCanvas({
  payload,
  templateId,
  width,
  className,
  onReady,
}: TemplateCanvasProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const messageCounter = useRef(0);
  const handshakeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const effectivePayload = useMemo<ArtboardPayload>(
    () => ({
      ...payload,
      organization: applyBrandingSnapshot(
        payload.organization,
        payload.invoice.brandingSnapshot ?? null,
      ),
      ...(templateId ? { templateId } : {}),
    }),
    [payload, templateId],
  );

  const iframeSrc = useMemo(() => '/artboard/invoice/preview?surface=plain', []);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      const data = event.data;
      if (!data || typeof data !== 'object') return;
      const message = data as ArtboardOutbound;
      if (message.type === 'INVOICE_RECEIVED') {
        if (handshakeTimer.current) {
          clearTimeout(handshakeTimer.current);
          handshakeTimer.current = null;
        }
        return;
      }
      if (message.type === 'RENDER_READY') {
        setPageCount(message.pageCount);
        onReady?.(message.pageCount);
      }
    };
    window.addEventListener('message', handler);
    return () => {
      window.removeEventListener('message', handler);
    };
  }, [onReady]);

  useEffect(() => {
    if (!iframeLoaded) return;
    const target = iframeRef.current?.contentWindow;
    if (!target) return;
    const id = `m-${messageCounter.current++}`;
    const message: ArtboardInbound = {
      type: 'SET_INVOICE',
      id,
      payload: effectivePayload,
    };
    try {
      target.postMessage(message, '*');
      if (handshakeTimer.current) clearTimeout(handshakeTimer.current);
      handshakeTimer.current = setTimeout(() => {
        try {
          target.postMessage(message, '*');
        } catch {
          /* iframe is gone */
        }
      }, 1000);
    } catch {
      /* same-origin postMessage shouldn't throw */
    }
    return () => {
      if (handshakeTimer.current) {
        clearTimeout(handshakeTimer.current);
        handshakeTimer.current = null;
      }
    };
  }, [iframeLoaded, effectivePayload]);

  useLayoutEffect(() => {
    try {
      const lf = iframeRef.current?.contentWindow?.localStorage;
      if (lf) lf.setItem(ARTBOARD_PAYLOAD_STORAGE_KEY, JSON.stringify(effectivePayload));
    } catch {
      /* not loaded yet */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scale = previewCanvasScale(width);
  const { intrinsicHeight } = scaledPreviewDimensions(width, pageCount);

  return (
    <PreviewCanvasFrame width={width} pageCount={pageCount || 1} className={className}>
      <iframe
        ref={iframeRef}
        src={iframeSrc}
        title="Invoice preview"
        onLoad={() => setIframeLoaded(true)}
        style={{
          width: A4_WIDTH_PX,
          height: intrinsicHeight,
          border: '0',
          transform: `scale(${scale})`,
          transformOrigin: '0 0',
          background: '#FFFFFF',
          display: 'block',
        }}
        scrolling="no"
        referrerPolicy="no-referrer"
      />
      {!iframeLoaded ? (
        <div className="absolute inset-0 flex items-center justify-center bg-white">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Rendering preview…
          </div>
        </div>
      ) : null}
    </PreviewCanvasFrame>
  );
}
